import { useEffect, useMemo, useState } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import type { Store } from '~/constants/stores';
import { storeStatus, type StoreSchedule } from '~/utils/live-host/store-status';

type OpenLabel = 'Opening Soon' | 'Open' | 'Closing Soon' | 'Closed';

// Darkest shade of each hue that still reaches 7:1 on white (AAA); a brighter
// yellow can't, so "soon" uses a dark amber. On the selected (blue) tile the
// label is white instead — none of these hues reach 7:1 on #0f52ba.
const OPEN_LABEL_COLOURS: Record<OpenLabel, string> = {
  Open: 'text-[#166534]',
  'Opening Soon': 'text-[#92400E]',
  'Closing Soon': 'text-[#92400E]',
  Closed: 'text-[#991B1B]',
};

// Closed until 30 minutes before opening, closing soon in the last 60 minutes.
// Times are the store's own timezone (storeStatus), not the visitor's clock.
// Same thresholds as Embarc's store selector.
function openLabel(
  schedule: StoreSchedule | undefined,
  now: number,
  fallbackTimezone?: string,
): OpenLabel | null {
  if (!schedule) return null;
  const status = storeStatus(schedule, now, {
    fallbackTimezone: fallbackTimezone || 'America/Los_Angeles',
  });
  if (status.state === 'open') {
    return status.minutesToClose !== undefined && status.minutesToClose <= 60
      ? 'Closing Soon'
      : 'Open';
  }
  if (status.state === 'closed') {
    return status.minutesToOpen !== undefined && status.minutesToOpen <= 30
      ? 'Opening Soon'
      : 'Closed';
  }
  return null;
}

// Haversine, in miles.
function milesBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3959;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// One decimal under 10 miles, whole numbers above. 9.96 rounds to "10", not "10.0".
function formatMiles(distance: number): string {
  const tenths = Math.round(distance * 10) / 10;
  return tenths < 10 ? tenths.toFixed(1) : Math.round(distance).toLocaleString('en-US');
}

/**
 * Street on one line, "City, ST ZIP" on the next. Prefers the structured
 * parts; otherwise the last two comma pieces of `address` are the city and
 * "ST ZIP", so a street with its own comma ("9032 Artesia Blvd, Bldg B")
 * stays together.
 */
function addressLines(store: Store): [string, string] {
  const p = store.addressParts;
  if (p) return [p.street, `${p.city}, ${p.state} ${p.zip}`];
  const parts = store.address
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 3) return [store.address, ''];
  return [parts.slice(0, -2).join(', '), parts.slice(-2).join(', ')];
}

interface StorePickerListProps {
  stores: Store[];
  chosen: string;
  onChoose: (value: string) => void;
}

/**
 * The store list shared by the age gate and the header store picker: a "use
 * my current location" control on top, then one tile per store — name on its
 * own line, hours and open status down the left, address and distance down
 * the right. Tiles grow vertically rather than wrapping text sideways.
 */
export default function StorePickerList({ stores, chosen, onChoose }: StorePickerListProps) {
  const rootData = useRouteLoaderData('root') as
    | { storeSchedules?: Record<string, StoreSchedule>; siteTimezone?: string }
    | undefined;

  // Client-only clock: the server's render time would mismatch on hydration
  // near a boundary, so labels appear after mount and refresh every minute.
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const openLabels = useMemo(() => {
    const labels: Record<string, OpenLabel | null> = {};
    if (now === null) return labels;
    for (const [id, schedule] of Object.entries(rootData?.storeSchedules ?? {})) {
      labels[id] = openLabel(schedule, now, rootData?.siteTimezone);
    }
    return labels;
  }, [now, rootData?.storeSchedules, rootData?.siteTimezone]);

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  const handleUseLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Your browser does not support location. Please pick a store below.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        setLocationError(
          error.code === error.PERMISSION_DENIED
            ? 'Location access is turned off. Please enable it or pick a store below.'
            : 'We couldn’t get your location. Please pick a store below.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  const clearLocation = () => {
    setUserLocation(null);
    setLocationError('');
  };

  // Nearest first once a location is set (stores without coordinates last),
  // otherwise the order the root loader sent.
  const list = useMemo(() => {
    const withDistance = stores.map((store) => ({
      store,
      distance: userLocation && store.geo ? milesBetween(userLocation, store.geo) : undefined,
    }));
    if (!userLocation) return withDistance;
    return [...withDistance].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }, [stores, userLocation]);

  const focusRing =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]';

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        {userLocation ? (
          <div className="flex items-center gap-2">
            <p className="text-sm text-black">Showing stores nearest you</p>
            <button
              type="button"
              onClick={clearLocation}
              className={`min-h-[44px] px-2 text-sm text-black underline hover:no-underline ${focusRing}`}
            >
              Clear
              <span className="sr-only"> current location</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleUseLocation}
            disabled={isLocating}
            className={`min-h-[44px] text-sm font-bold text-black underline hover:no-underline disabled:opacity-50 ${focusRing}`}
          >
            <span aria-hidden="true">📍 </span>
            {isLocating ? 'Getting your location…' : 'Use my current location'}
          </button>
        )}
        <p className="text-sm text-black">
          <span aria-hidden="true">Sort: {userLocation ? 'Near → Far' : 'A → Z'}</span>
          <span className="sr-only">
            {userLocation ? 'Stores sorted nearest first' : 'Stores sorted alphabetically'}
          </span>
        </p>
      </div>
      {locationError && (
        <p role="alert" className="mb-3 text-sm text-[#991B1B]">
          {locationError}
        </p>
      )}

      <div className="grid gap-2 lg:gap-4" role="listbox" aria-label="Available stores">
        {list.map(({ store, distance }) => {
          const selected = chosen === store.value;
          const [street, cityLine] = addressLines(store);
          const label = store.comingSoon ? null : openLabels[store.value];
          return (
            <button
              key={store.value}
              type="button"
              data-store-value={store.value}
              onClick={() => !store.comingSoon && onChoose(store.value)}
              disabled={store.comingSoon}
              className={`w-full p-4 px-6 text-start shadow-sm shadow-gray-400 transition ${focusRing} ${
                store.comingSoon
                  ? 'cursor-not-allowed bg-gray-100 text-black opacity-60'
                  : selected
                    ? 'bg-catalystdarkblue text-white'
                    : 'text-black hover:bg-gray-100'
              }`}
              role="option"
              aria-selected={selected}
              aria-disabled={store.comingSoon}
            >
              <span className="flex items-center justify-between gap-4">
                <span className="text-sm font-bold">{store.name.toUpperCase()}</span>
                {store.comingSoon && (
                  <span className="rounded bg-yellow-400 px-2 py-0.5 text-xs font-bold whitespace-nowrap text-black">
                    COMING SOON
                  </span>
                )}
              </span>
              <span className="font-helvetica-neue mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 text-xs leading-5 font-normal">
                <span className="block">
                  {store.hours.split(' | ').map((line) => (
                    <span key={line} className="block whitespace-nowrap">
                      {line}
                    </span>
                  ))}
                  {label && (
                    <span
                      className={`block whitespace-nowrap ${selected ? 'text-white' : OPEN_LABEL_COLOURS[label]}`}
                    >
                      <span aria-hidden="true">● </span>
                      {label}
                    </span>
                  )}
                </span>
                <span className="block text-right">
                  <span className="block">{street}</span>
                  {cityLine && <span className="block">{cityLine}</span>}
                  {distance !== undefined && (
                    <span className="block whitespace-nowrap">
                      {formatMiles(distance)} miles away
                    </span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}
